import React, { useState, useRef, useEffect, useCallback } from "react";
import Dropzone from "react-dropzone";
import remove from "../../../assets/Images/remove.svg";
import { DragOnDropLabel, Item, TextContainer } from "./style";
import Toast from "../../Toast/Toast";
import errorIcon from "../../Toast/Image/error.svg";

type TInputFileMultiple = {
  onChange: (files: FileList | null) => void;
  onDelete: (url: string) => Promise<number>;
  callbackFiles?: { nomeArquivo: string; url: string }[];
};

export function InputFileMultiple({
  onChange,
  onDelete,
  callbackFiles = [],
}: TInputFileMultiple) {
  const [inputFiles, setInputFiles] = useState<FileList | null>(null);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [list, setList] = useState<any>([]);
  const filteredCallbackFiles = callbackFiles.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t.nomeArquivo === item.nomeArquivo)
  );

  useEffect(() => {
    onChange(inputFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputFiles]);

  const showToast = useCallback(
    (type: any) => {
      let toastProperties: {
        id: number;
        title: string;
        description: string;
        backgroundColor: string;
        icon: any;
      } | null = null;

      const id = Math.floor(Math.random() * 101 + 1);
      switch (type) {
        case "error":
          toastProperties = {
            id,
            title: "",
            description: "Formato inválido, apenas PDF, PNG, JPG e JPEG",
            backgroundColor: "#D72828",
            icon: errorIcon,
          };
          break;
        case "maxSize":
          toastProperties = {
            id,
            title: "",
            description: "Tamanho máximo de 5mb por arquivo",
            backgroundColor: "#D72828",
            icon: errorIcon,
          };
          break;
        case "maxFiles":
          toastProperties = {
            id,
            title: "",
            description: "Máximo de 5 arquivos permitidos",
            backgroundColor: "#D72828",
            icon: errorIcon,
          };
          break;

        default:
          setList([]);
      }

      setList([...list, toastProperties]);
    },
    [list]
  );

  const acceptedFiles = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
  ];

  const innerRef = useRef<HTMLInputElement>(null);
  const handleOnChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const list = new DataTransfer();
    const { files } = event.target;
    const length = files?.length || 0;
    const names = [];
    if (inputFiles && files && inputFiles.length + files.length > 5) {
      showToast("maxFiles");
      return;
    }

    if (inputFiles && inputFiles.length > 0) {
      for (var i = 0; i < inputFiles.length; i++) {
        var fileItem = inputFiles.item(i);
        // eslint-disable-next-line no-loop-func
        if (fileItem && !fileNames.find((val) => val === fileItem?.name)) {
          list.items.add(fileItem);
          names.push(fileItem.name);
        }
      }
    }

    if (files && length > 0) {
      for (var i = 0; i < length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-redeclare
        var fileItem = files.item(i);

        // eslint-disable-next-line no-loop-func
        if (fileItem && !fileNames.find((val) => val === fileItem?.name)) {
          list.items.add(fileItem);
          names.push(fileItem.name);
        }
      }
    }
    const newFiles = list.files;
    setInputFiles(newFiles);
    setFileNames(names);
  };

  const resetInputFile = () => {
    const hiddenInputFile = document.getElementById(
      "fileMultiple"
    ) as HTMLInputElement | null;
    if (hiddenInputFile) {
      hiddenInputFile.value = "";
    }
  };

  const handleFileRemove = (index: number) => {
    const list = new DataTransfer();
    const names = [];
    if (inputFiles) {
      for (var i = 0; i < inputFiles?.length; i++) {
        if (i !== index) {
          var fileItem = inputFiles.item(i);

          if (fileItem !== null) {
            list.items.add(fileItem);
            names.push(fileItem.name);
          }
        }
      }
      const newFiles = list.files;
      if (newFiles.length > 0) {
        setInputFiles(newFiles);
        setFileNames(names);
      } else {
        setInputFiles(null);
        setFileNames([]);
        resetInputFile();
      }

      if (innerRef && innerRef.current) {
        innerRef.current.files = newFiles;
      }
    }
  };

  const onDragFile = (fileArr: File[]) => {
    console.log("ondrag: ", fileArr);
    const list = new DataTransfer();
    const names = [];
    if (fileArr.length + (inputFiles?.length || 0) + callbackFiles.length > 5) {
      showToast("maxFiles");
      return;
    }
    if (inputFiles && inputFiles.length > 0) {
      for (var i = 0; i < inputFiles.length; i++) {
        const fileItem = inputFiles.item(i);
        if (fileItem !== null) {
          list.items.add(fileItem);
          names.push(fileItem.name);
        }
      }
    }
    // eslint-disable-next-line array-callback-return
    fileArr.map((item) => {
      if (!(fileNames.find((val) => val === item.name) !== undefined)) {
        if (fileNames.length + filteredCallbackFiles.length < 5) {
          if (acceptedFiles.find((val) => val === item.type) !== undefined) {
            if (item.size <= 5 * 1024 * 1024) {
              list.items.add(item);
              names.push(item.name);
            } else {
              showToast("maxSize");
            }
          } else {
            showToast("error");
          }
        } else {
          showToast("maxFiles");
        }
      }
    });
    setFileNames(names);
    setInputFiles(list.files);
  };

  const handleDownloadFile = (url: string, filename: string) => {
    if (url) {
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const handleDeleteFile = async (url: string) => {
    await onDelete(url);
  };

  return (
    <>
      <Toast toastList={list} autoDelete={true} />

      <Dropzone onDrop={onDragFile}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <>
            <input
              type="file"
              multiple
              onChange={handleOnChangeFile}
              id="fileMultiple"
              accept="image/jpeg, image/png, image/jpg, application/pdf"
              ref={innerRef}
              {...getInputProps()}
            />
            <DragOnDropLabel
              {...getRootProps()}
              className={`${isDragActive ? "active" : ""}`}
            >
              {inputFiles === null && filteredCallbackFiles.length === 0 ? (
                <div className="container">
                  <p>
                    Arraste seu arquivo aqui ou{" "}
                    <span className="underline">selecione</span>, máx 5Mb
                  </p>
                  <p>São permitidos apenas PDF, PNG, JPG, JPEG</p>
                </div>
              ) : (
                <>
                  {filteredCallbackFiles.length > 0 &&
                    filteredCallbackFiles.map((item, index) => (
                      <Item key={`callback-${index}`}>
                        <TextContainer>{item.nomeArquivo}</TextContainer>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDownloadFile(item.url, item.nomeArquivo);
                          }}
                        >
                          Visualizar
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteFile(item.url);
                          }}
                        >
                          <img src={remove} width={12} height={12} alt="" />
                        </button>
                      </Item>
                    ))}
                  {fileNames.map((item, index) => (
                    <Item key={`file-${index}`}>
                      <TextContainer>{item}</TextContainer>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleFileRemove(Number(index));
                        }}
                      >
                        <img src={remove} width={12} height={12} alt="" />
                      </button>
                    </Item>
                  ))}
                </>
              )}
            </DragOnDropLabel>
          </>
        )}
      </Dropzone>
    </>
  );
}
